/**
 * Button atom - The foundation button component.
 * All buttons in the app should use this.
 *
 * Features:
 * - Micro-interaction animations (scale on press)
 * - Smooth transitions
 * - Loading and success states
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import Spinner from './Spinner';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  isSuccess?: boolean;
  fullWidth?: boolean;
  /** Leading icon (lucide element, sized by the caller) */
  icon?: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      isSuccess = false,
      fullWidth = false,
      icon,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = `
      inline-flex items-center justify-center font-semibold
      rounded-2xl
      transition-all duration-150 ease-out
      focus:outline-none focus:ring-2 focus:ring-offset-2
      disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
      active:scale-[0.97]
      tap-highlight-none
    `;

    const variantStyles = {
      primary: `
        bg-primary text-on-primary
        hover:bg-primary-hover
        active:bg-primary-active
        focus:ring-primary
        shadow-btn
      `,
      secondary: `
        bg-secondary text-on-primary
        hover:bg-secondary-hover hover:shadow-elevation-1
        focus:ring-secondary
        shadow-sm
      `,
      ghost: `
        bg-transparent text-text
        hover:bg-surface-hover
        active:bg-surface-active
        focus:ring-primary
      `,
      danger: `
        bg-error text-on-primary
        hover:brightness-110 hover:shadow-elevation-1
        active:brightness-95
        focus:ring-error
        shadow-sm
      `,
    };

    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm gap-1.5',
      md: 'px-5 py-2.5 text-base gap-2',
      lg: 'px-7 py-3.5 text-lg gap-2.5',
    };

    const widthStyles = fullWidth ? 'w-full' : '';

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading || isSuccess}
        className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyles} ${className}`}
        {...props}
      >
        {isSuccess ? (
          <>
            <Check size={20} className="animate-scale-in" />
            <span>Done</span>
          </>
        ) : isLoading ? (
          <>
            <Spinner size="sm" className="border-current" />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {icon}
            {children}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
